import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const API = 'https://melvo-backend-production.up.railway.app';

const styles = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#060608',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'Geist, sans-serif',
    padding: '24px',
  },
  card: {
    backgroundColor: '#0c0c10',
    border: '1px solid #1a1a24',
    borderRadius: '16px',
    padding: '48px 40px',
    width: '100%',
    maxWidth: '420px',
  },
  logo: {
    color: '#00e5a0',
    fontSize: '22px',
    fontWeight: '700',
    letterSpacing: '-0.5px',
    marginBottom: '40px',
    display: 'block',
  },
  heading: {
    color: '#f0f0f8',
    fontSize: '24px',
    fontWeight: '600',
    marginBottom: '8px',
    lineHeight: '1.3',
  },
  subtext: {
    color: '#6a6a84',
    fontSize: '14px',
    marginBottom: '32px',
    lineHeight: '1.6',
  },
  label: {
    display: 'block',
    color: '#6a6a84',
    fontSize: '13px',
    marginBottom: '8px',
    fontWeight: '500',
  },
  input: {
    width: '100%',
    backgroundColor: '#060608',
    border: '1px solid #1a1a24',
    borderRadius: '8px',
    padding: '12px 16px',
    color: '#f0f0f8',
    fontSize: '16px',
    fontFamily: 'Geist, sans-serif',
    outline: 'none',
    boxSizing: 'border-box',
    marginBottom: '16px',
  },
  button: {
    width: '100%',
    backgroundColor: '#00e5a0',
    color: '#060608',
    border: 'none',
    borderRadius: '8px',
    padding: '13px',
    fontSize: '15px',
    fontWeight: '600',
    fontFamily: 'Geist, sans-serif',
    cursor: 'pointer',
    marginTop: '8px',
  },
  buttonDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  otpRow: {
    display: 'flex',
    gap: '10px',
    marginBottom: '16px',
  },
  otpInput: {
    flex: 1,
    backgroundColor: '#060608',
    border: '1px solid #1a1a24',
    borderRadius: '8px',
    padding: '14px 0',
    color: '#f0f0f8',
    fontSize: '22px',
    fontFamily: 'Geist, sans-serif',
    fontWeight: '600',
    textAlign: 'center',
    outline: 'none',
    width: '100%',
  },
  error: {
    color: '#ff6b6b',
    fontSize: '13px',
    marginBottom: '12px',
  },
  notice: {
    color: '#6a6a84',
    fontSize: '13px',
    marginBottom: '12px',
    lineHeight: '1.6',
    backgroundColor: '#0f0f16',
    borderRadius: '8px',
    padding: '12px 14px',
    border: '1px solid #1a1a24',
  },
  resend: {
    background: 'none',
    border: 'none',
    color: '#00e5a0',
    fontSize: '13px',
    cursor: 'pointer',
    fontFamily: 'Geist, sans-serif',
    padding: 0,
    marginTop: '12px',
    display: 'block',
  },
  resendDisabled: {
    color: '#6a6a84',
    cursor: 'default',
  },
  support: {
    marginTop: '32px',
    paddingTop: '24px',
    borderTop: '1px solid #1a1a24',
    color: '#6a6a84',
    fontSize: '13px',
    textAlign: 'center',
  },
  supportLink: {
    color: '#00e5a0',
    textDecoration: 'none',
  },
  newAccountNote: {
    marginTop: '24px',
    color: '#6a6a84',
    fontSize: '13px',
    textAlign: 'center',
    lineHeight: '1.6',
  },
};

export default function LoginPage() {
  const navigate = useNavigate();

  const [step, setStep] = useState('phone'); // phone | otp
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);

  // Redirect if already logged in
  useEffect(() => {
    const token = localStorage.getItem('melvo_token');
    if (token) navigate('/dashboard');
  }, [navigate]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  async function requestOtp() {
    setErrorMsg('');
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length < 10) {
      setErrorMsg('Enter a valid US phone number.');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/otp/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (res.status === 429) {
          setErrorMsg('Too many attempts. Please wait before requesting a new code.');
        } else if (res.status === 404) {
          // No account found — per spec: webhook delay message
          setErrorMsg(
            'Your account is still being set up — this usually takes less than 30 seconds. Try signing in again shortly.'
          );
        } else {
          setErrorMsg(data.error || 'Something went wrong. Please try again.');
        }
        return;
      }
      setStep('otp');
      setResendCooldown(30);
    } catch {
      setErrorMsg('Unable to send code. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  async function verifyOtp(codeStr) {
    setErrorMsg('');
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/otp/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone, code: codeStr }),
      });
      const data = await res.json();
      if (!res.ok) {
        setErrorMsg(
          res.status === 401
            ? 'Incorrect code. Please check your text and try again.'
            : data.error || 'Verification failed. Please try again.'
        );
        setOtp(['', '', '', '', '', '']);
        document.getElementById('otp-0')?.focus();
        return;
      }
      localStorage.setItem('melvo_token', data.token);
      if (data.onboarding_complete === false) {
        navigate('/onboarding');
      } else {
        navigate('/dashboard');
      }
    } catch {
      setErrorMsg('Unable to verify. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  function handleOtpChange(index, value) {
    if (!/^\d*$/.test(value)) return;
    const next = [...otp];
    next[index] = value.slice(-1);
    setOtp(next);
    if (value && index < 5) {
      document.getElementById(`otp-${index + 1}`)?.focus();
    }
    if (index === 5 && value) {
      const code = [...next].join('');
      if (code.length === 6) verifyOtp(code);
    }
  }

  function handleOtpKeyDown(index, e) {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      document.getElementById(`otp-${index - 1}`)?.focus();
    }
  }

  function handleOtpPaste(e) {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      const next = pasted.split('');
      setOtp(next);
      document.getElementById('otp-5')?.focus();
      verifyOtp(pasted);
    }
  }

  // ── Render ────────────────────────────────────────────────

  if (step === 'phone') {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <a href="https://melvo.io" target="_blank" rel="noreferrer" style={{ display: "block", marginBottom: 32 }}><img src="/melvo-logo.png" alt="Melvo" style={{ height: 80 }} /></a>
          <h1 style={styles.heading}>Welcome back</h1>
          <p style={styles.subtext}>
            Enter your mobile number to sign in.
          </p>
          {errorMsg && <p style={styles.error}>{errorMsg}</p>}
          <label style={styles.label} htmlFor="phone">Mobile number</label>
          <input
            id="phone"
            type="tel"
            placeholder="+1 (612) 555-0123"
            value={phone}
            onChange={e => setPhone(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && requestOtp()}
            style={styles.input}
            autoFocus
          />
          <button
            onClick={requestOtp}
            disabled={loading}
            style={{ ...styles.button, ...(loading ? styles.buttonDisabled : {}) }}
          >
            {loading ? 'Sending…' : 'Send Code'}
          </button>
          <p style={styles.newAccountNote}>
            New to Melvo? Start with the link in your welcome email.
          </p>
          <div style={{ marginTop: 24, borderTop: "1px solid #1a1a24", paddingTop: 20, display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap", justifyContent: "center" }}>
              <a href="https://melvo.io/privacy-policy" target="_blank" rel="noreferrer" style={{ color: "#6a6a84", fontSize: 11, textDecoration: "none" }}>Privacy Policy</a>
              <a href="https://melvo.io/terms-of-service" target="_blank" rel="noreferrer" style={{ color: "#6a6a84", fontSize: 11, textDecoration: "none" }}>Terms of Service</a>
              <a href="https://melvo.io/messaging-policy" target="_blank" rel="noreferrer" style={{ color: "#6a6a84", fontSize: 11, textDecoration: "none" }}>Messaging Policy</a>
              <a href="mailto:hello@melvo.io" style={{ color: "#6a6a84", fontSize: 11, textDecoration: "none" }}>hello@melvo.io</a>
            </div>
            <span style={{ color: "#3a3a54", fontSize: 10 }}>© 2026 Nordeast Productions LLC DBA Melvo</span>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'otp') {
    const digits = phone.replace(/\D/g, '');
    const formatted = digits.length === 10
      ? `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`
      : phone;

    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <a href="https://melvo.io" target="_blank" rel="noreferrer" style={{ display: "block", marginBottom: 32 }}><img src="/melvo-logo.png" alt="Melvo" style={{ height: 80 }} /></a>
          <h1 style={styles.heading}>Check your texts</h1>
          <p style={styles.subtext}>
            We sent a 6-digit code to {formatted}.
          </p>
          {errorMsg && <p style={styles.error}>{errorMsg}</p>}
          <div style={styles.otpRow} onPaste={handleOtpPaste}>
            {otp.map((digit, i) => (
              <input
                key={i}
                id={`otp-${i}`}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={e => handleOtpChange(i, e.target.value)}
                onKeyDown={e => handleOtpKeyDown(i, e)}
                style={styles.otpInput}
                autoFocus={i === 0}
                disabled={loading}
              />
            ))}
          </div>
          <button
            style={{ ...styles.button, ...(loading ? styles.buttonDisabled : {}) }}
            disabled={loading || otp.join('').length < 6}
            onClick={() => verifyOtp(otp.join(''))}
          >
            {loading ? 'Verifying…' : 'Verify'}
          </button>
          <button
            style={{ ...styles.resend, ...(resendCooldown > 0 ? styles.resendDisabled : {}) }}
            disabled={resendCooldown > 0}
            onClick={() => {
              setOtp(['', '', '', '', '', '']);
              setErrorMsg('');
              setStep('phone');
            }}
          >
            {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Didn't get it? Go back"}
          </button>
          <div style={styles.support}>
            Questions? <a href="mailto:hello@melvo.io" style={styles.supportLink}>hello@melvo.io</a>
          </div>
          <div style={{ marginTop: 24, borderTop: "1px solid #1a1a24", paddingTop: 20, display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap", justifyContent: "center" }}>
              <a href="https://melvo.io/privacy-policy" target="_blank" rel="noreferrer" style={{ color: "#6a6a84", fontSize: 11, textDecoration: "none" }}>Privacy Policy</a>
              <a href="https://melvo.io/terms-of-service" target="_blank" rel="noreferrer" style={{ color: "#6a6a84", fontSize: 11, textDecoration: "none" }}>Terms of Service</a>
              <a href="https://melvo.io/messaging-policy" target="_blank" rel="noreferrer" style={{ color: "#6a6a84", fontSize: 11, textDecoration: "none" }}>Messaging Policy</a>
              <a href="mailto:hello@melvo.io" style={{ color: "#6a6a84", fontSize: 11, textDecoration: "none" }}>hello@melvo.io</a>
            </div>
            <span style={{ color: "#3a3a54", fontSize: 10 }}>© 2026 Nordeast Productions LLC DBA Melvo</span>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
